import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { BotChatService } from '../../../services/libs/bot-chat.service';
import { TagManagerService } from '../../../services/tms/tag-manager.service';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { AsyncPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { PickaxeWidgetPatch } from './pickaxe-widget-patch';

const FOCUSABLE_SELECTOR: string = 'iframe, input, textarea, button';

@Component({
  selector: 'app-bot-chat',
  templateUrl: './bot-chat.component.html',
  styleUrls: ['./bot-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AsyncPipe, TranslatePipe],
})
export class BotChatComponent implements OnDestroy {
  private readonly _botChat: BotChatService = inject(BotChatService);
  private readonly _tagManager: TagManagerService = inject(TagManagerService);
  private readonly _platformId: object = inject<object>(PLATFORM_ID);

  public readonly deploymentId: string = environment.mavicBotDeploymentId ?? '';
  public readonly accepted$: Observable<boolean> = this._botChat.accepted$;
  public open: boolean = false;
  public pulsing: boolean = true;

  @ViewChild('panel') private readonly _panel?: ElementRef<HTMLElement>;
  @ViewChild('closeBtn') private readonly _closeBtn?: ElementRef<HTMLElement>;
  @ViewChild('deploy') private readonly _deploy?: ElementRef<HTMLElement>;

  private _patch?: PickaxeWidgetPatch;

  public ngOnDestroy(): void {
    this._patch?.destroy();
  }

  public toggle(accepted: boolean | null): void {
    if (!accepted) {
      this._tagManager.promptCookies();
      return;
    }

    if (this.open) {
      this.close();
    } else {
      this.openPanel();
    }
  }

  public close(): void {
    this.open = false;
    this._patch?.onClose();
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.open) {
      this.close();
    }
  }

  private openPanel(): void {
    this.open = true;
    this.pulsing = false;

    this.patch()?.onOpen();

    const focusable: HTMLElement | null | undefined =
      this._panel?.nativeElement.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable) {
      setTimeout(() => focusable.focus({ preventScroll: true }), 250);
    }
  }

  private patch(): PickaxeWidgetPatch | undefined {
    if (this._patch) {
      return this._patch;
    }
    if (!isPlatformBrowser(this._platformId)) {
      return undefined;
    }

    const panel: HTMLElement | undefined = this._panel?.nativeElement;
    const closeBtn: HTMLElement | undefined = this._closeBtn?.nativeElement;
    if (!panel || !closeBtn) {
      return undefined;
    }

    this._patch = new PickaxeWidgetPatch({
      panel,
      closeBtn,
      deployEl: this._deploy?.nativeElement ?? null,
    });
    return this._patch;
  }
}
